import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilmCard from "@/components/feature/FilmCard";
import TopicCard from "@/components/feature/TopicCard";
import ShareButtons from "@/components/feature/ShareButtons";
import SectionHeading from "@/components/feature/SectionHeading";
import ContentMissing from "@/components/feature/ContentMissing";
import { getStarById, getStarFilms, getTopicsByIds } from "@/lib/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function StarDetail() {
  const { id } = useParams<{ id: string }>();
  const star = id ? getStarById(id) : undefined;

  useDocumentMeta({
    title: star ? `${star.name} - 巨乳娱乐资讯网` : "明星人物 - 巨乳娱乐资讯网",
    description: star
      ? `${star.name}人物资料：${star.nationality}${star.profession}，代表作品包括${getStarFilms(star)
          .map((film) => film.title)
          .join("、")}。`
      : "明星人物详情页，聚合演员资料、代表作品与相关话题。",
    keywords: star
      ? `${star.name},明星资料,${star.nationality},代表作品,巨乳娱乐资讯网`
      : "明星人物,明星资料,巨乳娱乐资讯网",
  });

  if (!star) {
    return <ContentMissing message="该明星人物资料可能已被移除，或链接有误。" />;
  }

  const works = getStarFilms(star);
  const relatedTopics = getTopicsByIds(star.topicIds);
  const workTitles = works.map((film) => film.title);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "明星人物", to: "/category/stars" },
          { label: star.name },
        ]}
      />

      <section className="mt-4 flex flex-col gap-6 rounded-lg border border-background-200 bg-background-50 p-5 md:flex-row md:p-6">
        <div className="mx-auto h-32 w-32 shrink-0 overflow-hidden rounded-full bg-background-100 md:mx-0 md:h-40 md:w-40">
          <img
            src={star.avatar}
            alt={`${star.name} 明星头像`}
            title={`${star.name} 明星人物 巨乳娱乐资讯网`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="flex flex-1 flex-col gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {star.name}
              </h1>
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-700">
                {star.profession}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {star.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <div className="flex gap-2">
              <dt className="shrink-0 text-foreground-500">国籍：</dt>
              <dd className="text-foreground-800">{star.nationality}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 text-foreground-500">性别：</dt>
              <dd className="text-foreground-800">{star.gender}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 text-foreground-500">出生日期：</dt>
              <dd className="text-foreground-800">{star.birthDate}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 text-foreground-500">代表作品：</dt>
              <dd className="text-foreground-800">
                {workTitles.join("、") || "暂无"}
              </dd>
            </div>
          </dl>

          <ShareButtons title={`${star.name} - 巨乳娱乐资讯网`} />
        </div>
      </section>

      <section className="mt-10">
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
          <span className="inline-block h-5 w-1 rounded-full bg-accent-500" aria-hidden="true"></span>
          详细简介与职业经历
        </h2>
        <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-5 text-sm leading-relaxed text-foreground-700 md:text-base">
          {star.bio.map((paragraph) => (
            <p key={paragraph.slice(0, 12)}>{paragraph}</p>
          ))}
        </div>
      </section>

      {works.length > 0 && (
        <section className="mt-10">
          <SectionHeading
            title="代表影视作品"
            subtitle="点击卡片查看作品详情"
            moreTo="/category/films"
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {works.map((film) => (
              <FilmCard key={film.id} film={film} />
            ))}
          </div>
        </section>
      )}

      {relatedTopics.length > 0 && (
        <section className="mt-10">
          <SectionHeading
            title="相关新闻话题"
            subtitle="与该人物相关的讨论与报道"
            moreTo="/category/topics"
          />
          <div className="flex flex-col gap-3">
            {relatedTopics.map((topic) => (
              <TopicCard key={topic.id} topic={topic} variant="row" />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}